import { atomWithStorage } from "jotai/utils";

export type DatasetModeName =
  | "fast"
  | "balanced"
  | "limited"
  | "accuracy"
  | "comprehensive"
  | "advanced";

/** "server" keeps the Jev screen setting of the server, which differs per environment. */
export type ScreenSetting = "server" | "on" | "off";

export interface AdvancedSetup {
  /** Null keeps the chat's model. */
  chatModel: string | null;
  /** Null keeps the default map model. */
  model: string | null;
  /** Papers that a map reads at the same time. Null keeps the server default. */
  maxConcurrent: number | null;
  screen: ScreenSetting;
  /** Jev screen pass mark from 0 to 1 when the screen is "on". Null uses the default. */
  screenMin: number | null;
}

export interface DatasetModeState {
  mode: DatasetModeName;
  advanced: AdvancedSetup;
  /** The user picked the mode. Until then, Fast is the default where the dataset builder serves it. */
  chosen?: boolean;
}

export const DEFAULT_ADVANCED_SETUP: AdvancedSetup = {
  chatModel: null,
  model: null,
  maxConcurrent: null,
  screen: "server",
  screenMin: null,
};

export const DEFAULT_DATASET_MODE: DatasetModeState = {
  mode: "balanced",
  advanced: DEFAULT_ADVANCED_SETUP,
};

export const DEFAULT_CHAT_MODEL_LABEL = "Claude Opus 5";
export const DEFAULT_MAP_MODEL_LABEL = "Gemini 3.5 Flash Lite";
export const DEFAULT_PARALLEL_MAPS = 32;
export const DEFAULT_SCREEN_MIN = 0.3;
export const MAX_PARALLEL_MAPS = 128;

/** The same lists as ADVANCED_CHAT_MODELS and ADVANCED_MODELS in the backend's dataset_modes.py. */
export const ADVANCED_CHAT_MODELS = [
  { id: "anthropic-direct/claude-haiku-5.5", label: "Claude Haiku 5.5" },
  { id: "anthropic-direct/claude-sonnet-5", label: "Claude Sonnet 5" },
  { id: "anthropic-direct/claude-sonnet-5.5", label: "Claude Sonnet 5.5" },
  { id: "anthropic-direct/claude-opus-5", label: "Claude Opus 5" },
  { id: "anthropic-direct/claude-opus-5.5", label: "Claude Opus 5.5" },
] as const;

export const ADVANCED_MODELS = [
  { id: "google/gemini-3.5-flash", label: "Gemini 3.5 Flash" },
  ...ADVANCED_CHAT_MODELS,
] as const;

export interface DatasetModeOption {
  id: DatasetModeName;
  label: string;
  description: string;
  /** About how long a dataset takes, shown in the picker. */
  time?: string;
  /** Shown in the mode picker. The other modes still work from the CLI and the API. */
  listed: boolean;
  /** The mode needs the paperclipMapModels flag. */
  mapModels: boolean;
  /** Only GXL accounts with the routines flag can use the mode. */
  gxl: boolean;
  available: boolean;
}

/** What the user's account allows. The backend checks the same for each request. */
export interface DatasetModeAccess {
  mapModels: boolean;
  gxl: boolean;
  /** Fast mode comes from the dataset builder service (datasetBuilder). */
  fastBuilder?: boolean;
}

/** Fast mode as the dataset builder serves it. */
export const FAST_BUILDER_OPTION: Pick<DatasetModeOption, "description" | "time"> = {
  description:
    "Builds the table in seconds from papers: each value is the exact text of a paper. Papers only.",
  time: "~10 sec",
};

export const DATASET_MODES: readonly DatasetModeOption[] = [
  {
    id: "fast",
    label: "Fast",
    description: "For values stated plainly in the papers, or yes or no.",
    time: "~5 sec",
    listed: true,
    mapModels: true,
    gxl: false,
    available: true,
  },
  {
    id: "balanced",
    label: "Balanced",
    description: "For most questions. Reads each paper in full.",
    time: "~5 min",
    listed: true,
    mapModels: false,
    gxl: false,
    available: true,
  },
  {
    id: "limited",
    label: "Balanced (limited)",
    description:
      "GXL accounts only. Balanced, as accounts outside GXL see it: with their row limit, and without the map options that only GXL accounts have.",
    listed: false,
    mapModels: false,
    gxl: true,
    available: true,
  },
  {
    id: "accuracy",
    label: "Comprehensive",
    description: "For subtle or hard-to-find values.",
    time: "~20 min",
    listed: true,
    mapModels: true,
    gxl: false,
    available: true,
  },
  {
    id: "comprehensive",
    label: "Full read",
    description:
      "GXL accounts only. Optimizes the columns, then the search, then reads every paper of the cohort and checks coverage. Can take hours: send “continue” when a turn ends.",
    listed: false,
    mapModels: false,
    gxl: true,
    available: true,
  },
  {
    id: "advanced",
    label: "Advanced",
    description:
      "Choose the agent's model, how papers are read, how many are read at once, and how strictly papers are screened.",
    listed: false,
    mapModels: true,
    gxl: false,
    available: true,
  },
];

/** The listed modes that the user's account allows. A saved mode that is not listed falls back to Balanced. */
export const allowedDatasetModes = (access: DatasetModeAccess): DatasetModeOption[] =>
  DATASET_MODES.filter(
    (mode) =>
      mode.listed &&
      (access.mapModels || !mode.mapModels || (mode.id === "fast" && access.fastBuilder)) &&
      (access.gxl || !mode.gxl),
  ).map((mode) =>
    mode.id === "fast" && access.fastBuilder ? { ...mode, ...FAST_BUILDER_OPTION } : mode,
  );

export const datasetModeAtom = atomWithStorage<DatasetModeState>(
  "paperclip.datasetBuilder.mode",
  DEFAULT_DATASET_MODE,
  undefined,
  { getOnInit: true },
);

/**
 * The mode that applies: a saved mode that the user cannot use falls back to
 * Balanced. Where the dataset builder serves Fast, Fast is the default.
 */
export const effectiveDatasetMode = (
  state: DatasetModeState,
  access: DatasetModeAccess,
): DatasetModeName => {
  if (access.fastBuilder && !state?.chosen) return "fast";
  const option = allowedDatasetModes(access).find((mode) => mode.id === state?.mode);
  return option?.available ? option.id : "balanced";
};

/** Whether a new dataset in this mode is built by the dataset builder, not by the agent. */
export const usesFastBuilder = (state: DatasetModeState, access: DatasetModeAccess): boolean =>
  Boolean(access.fastBuilder) && effectiveDatasetMode(state, access) === "fast";

/** The `dataset_mode` of a chat request, or undefined for the default mode. */
export const datasetModeRequest = (
  state: DatasetModeState,
  access: DatasetModeAccess,
): Record<string, unknown> | undefined => {
  const mode = effectiveDatasetMode(state, access);
  // The dataset builder builds Fast datasets; their chat agent works as in Balanced.
  if (mode === "fast" && access.fastBuilder) return undefined;
  if (["fast", "limited", "accuracy", "comprehensive"].includes(mode)) return { mode };
  if (mode !== "advanced") return undefined;
  const setup = { ...DEFAULT_ADVANCED_SETUP, ...state.advanced };
  const request: Record<string, unknown> = { mode };
  if (setup.chatModel) request.chat_model = setup.chatModel;
  if (setup.model) request.model = setup.model;
  if (setup.maxConcurrent) request.max_concurrent = setup.maxConcurrent;
  if (setup.screen === "off") request.screen = false;
  if (setup.screen === "on") request.screen_min = setup.screenMin ?? DEFAULT_SCREEN_MIN;
  return request;
};
